/* eslint-disable react-hooks/rules-of-hooks */
import { injectable } from 'inversify';
import { RootState, useAppSelector } from '../../state/hooks';
import { GlobalQueryService } from '../../state/queryParams/global.query.service';
import {
  QueryService,
  GlobalQueryParams,
} from '../../state/queryParams/queryParams.interface';
import { createDeepEqualSelector } from '../../state/selector.helpers';
import { store } from '../../state/store';
import {
  assessmentSummaryPageQuerySelector,
  dispatchPageQuery,
} from './assessmentSummary.queryParam.slice';
import { AssessmentSummaryQuery } from './assessmentSummary.queryParams.interface';

// This is a "page query service". Its main purpose is to keep track of queryParams for this route and its relationship to global queryParams. It makes the page remember its previous queryParams when you get back. Add queryParam to the interface file and the getValidPageQueryParams method in this file.
// TODO: break out into shared service for all pages and only configure each.
@injectable()
export class AssessmentSummaryQueryService
  implements QueryService<AssessmentSummaryQuery & GlobalQueryParams>
{
  mergedQuerySelector!: (
    state: RootState,
  ) => AssessmentSummaryQuery & GlobalQueryParams;

  assessmentSummaryPageQuerySelector = assessmentSummaryPageQuerySelector;

  constructor(public globalQueryService: GlobalQueryService) {
    this.createMergeQueryParamsSelector();
  }

  useSelectMergedQuery = () => useAppSelector(this.mergedQuerySelector);

  useQuerySelector() {
    return useAppSelector(assessmentSummaryPageQuerySelector);
  }

  dispatchQueryParams = (
    queryParams: Partial<AssessmentSummaryQuery & GlobalQueryParams>,
  ) => {
    this.dispatchPageQuery(queryParams as Partial<AssessmentSummaryQuery>);

    this.globalQueryService.dispatchQueryParams(
      queryParams as Partial<GlobalQueryParams>,
    );
  };

  private createMergeQueryParamsSelector = () => {
    this.mergedQuerySelector = createDeepEqualSelector(
      assessmentSummaryPageQuerySelector,
      this.globalQueryService.globalQuerySelector,
      (
        assessmentSummaryQuery: AssessmentSummaryQuery,
        globalQuery: GlobalQueryParams,
      ): AssessmentSummaryQuery & GlobalQueryParams => {
        return {
          ...assessmentSummaryQuery,
          ...globalQuery,
        };
      },
    );
  };

  private dispatchPageQuery(queryParams: Partial<AssessmentSummaryQuery>) {
    const validQueryParams = this.getValidPageQueryParams(queryParams);
    const usedQueryParams = this.removeUnusedQueryParams(validQueryParams);

    store.dispatch(dispatchPageQuery(usedQueryParams));
  }

  private getValidPageQueryParams = (
    queryParams: Partial<AssessmentSummaryQuery>,
  ): AssessmentSummaryQuery => {
    return {
      selectedAssessmentId: queryParams.selectedAssessmentId,
    };
  };

  // removes unused properties so they don't overwrite storeParams in useQuerySync hook.
  private removeUnusedQueryParams = (
    validQueryParams: AssessmentSummaryQuery,
  ): Partial<AssessmentSummaryQuery> => {
    const usedQueryParams = {
      ...validQueryParams,
    };
    Object.keys(usedQueryParams).forEach(
      (key) =>
        usedQueryParams[key as keyof AssessmentSummaryQuery] === undefined &&
        delete usedQueryParams[key as keyof AssessmentSummaryQuery],
    );
    return usedQueryParams;
  };
}
